import { useCallback, useContext, useState } from 'react'

import { PointCloudOctree, Potree } from '@datalabs-jp/three-loader'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { ERROR_PROCESS, processErrorHandler } from '@editor/services/ErrorHandler'
import { getSignedUrlForGetDownSampledFile } from '@editor/services/InspectionArea'
import { LoaderParams, PotreeCache } from '@editor/services/PotreeCache'
import * as Sentry from '@sentry/react'
import { useParams } from 'react-router'
import { Cache, Points, PointsMaterial } from 'three'
import { PCDLoader } from 'three-stdlib'

export type ProgressCallback = (percent: number) => void

const usePCDLoader = (customPotree?: Potree) => {
  // URL Params
  const { project_id } = useParams<{ project_id: string }>()

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Potree stuff
  const [potree] = useState<Potree>(customPotree || new Potree('v2'))
  const [potreeCache] = useState<PotreeCache>(new PotreeCache(getAccessToken))

  /**
   * Load a Potree point cloud.
   *
   * @param loaderParams Parameters for loading. Must include inspection area.
   * @param blueprint Optional blueprint, if loading from a blueprint area.
   */
  const loadPotree = useCallback(
    async (loaderParams: LoaderParams): Promise<PointCloudOctree | Points | null> => {
      if (!loaderParams.inspectionArea.down_sampled_file || !project_id) {
        return null
      }

      // Get access token
      const pcd = await potree
        .loadPointCloud(
          // The name of the point cloud which is to be loaded.
          'metadata.json',
          // Given the relative URL of a file, should return a full URL (e.g. signed).
          async (fileName: string) => {
            const urls = await potreeCache.getPotreeSignedURLs(project_id, loaderParams, showErrorModal)
            const fileNameWithoutExt = fileName.split('.')[0]

            let url = ''
            if (urls && PotreeCache.hasFile(fileNameWithoutExt, urls)) {
              url = urls[fileNameWithoutExt]
            }

            return url
          },
        )
        .catch((err) => {
          Sentry.captureException(err)

          return null
        })

      if (pcd)
        pcd.name = [
          loaderParams.inspectionArea.inspection_area_id,
          loaderParams.blueprint?.blueprint_id,
          loaderParams.blueprintAreaId,
        ]
          .filter(Boolean)
          .join('-')
      return pcd
    },
    [potree, project_id, potreeCache, showErrorModal],
  )

  /**
   * Load a PCD file.
   *
   * @param loaderParams Parameters for loading. Must include inspection area.
   * @param onProgress Progress callback. Only used for PCDLoader, Potree has no progress.
   */
  const loadPCD = useCallback(
    async (loaderParams: LoaderParams, onProgress?: ProgressCallback): Promise<Points | PointCloudOctree | null> => {
      const { inspectionArea, blueprint } = loaderParams

      if (!inspectionArea.down_sampled_file || !project_id) {
        return null
      }

      // Potree-based files.
      // Blueprint will only load through Potree.
      const po3dir = inspectionArea.down_sampled_file.potree_dir
      if (po3dir || blueprint) {
        return loadPotree(loaderParams)
      }

      if (Cache.get(inspectionArea.inspection_area_id)) {
        const loadPoints = Cache.get(inspectionArea.inspection_area_id) as Points
        ;(loadPoints.material as PointsMaterial).transparent = true
        return loadPoints
      }

      // Get access token
      const token = await getAccessToken()
      if (!token) {
        return null
      }

      const url = await getSignedUrlForGetDownSampledFile(
        token,
        project_id,
        inspectionArea.inspection_area_id,
        showErrorModal,
      )
      if (!url) {
        return null
      }

      // 点群データのダウンロード
      try {
        const loader = new PCDLoader()
        let loadPoints: Points
        try {
          loadPoints = await loader.loadAsync(url, (e) => {
            onProgress?.((e.loaded / e.total) * 100)
          })
        } catch (err) {
          if (err instanceof RangeError) {
            showErrorModal(
              '読み込み可能な点群サイズの上限を超過しました。小さい範囲で点群を取り直すか、点群密度を間引いてお試しください。\nこの上限はお使いの端末の性能等により変動することがあります。',
            )
            return null
          }
          throw err
        }

        loadPoints.name = inspectionArea.inspection_area_id

        // 点群データをキャッシュに保存（どこかのタイミングでキャッシュを消したい、ログアウトとか？）
        Cache.add(inspectionArea.inspection_area_id, loadPoints)
        ;(loadPoints.material as PointsMaterial).transparent = true

        return loadPoints
      } catch (err) {
        Sentry.captureException(err)
        processErrorHandler(err, ERROR_PROCESS.GET_DOWN_SAMPLED_FILE, showErrorModal)
        return null
      }
    },
    [project_id, loadPotree, getAccessToken, showErrorModal],
  )

  return {
    potree,
    loadPCD,
    loadPotree,
  }
}

export default usePCDLoader
